/**
 * Billing constants and helpers.
 *
 * Plans are billed quarterly: Plus is shown as $7/month and charged $21 every 3
 * months, Premium $17/month charged $51 every 3 months. Upgrades go through a
 * server-created Stripe Checkout Session (see `startCheckout` in billing-api.ts) that
 * is bound to the signed-in user — never a static Payment Link, which can't carry the
 * account and so could leave a paid purchase unattached to anyone.
 */

import type { AuthUser } from "@/hooks/useAuth";

export type PlanId = "starter" | "plus" | "premium";

export interface PlanPricing {
  monthly: number;
  quarterlyTotal: number;
  quarterlyMonthlyEquivalent: number;
}

/**
 * What the plan promises, in card form. Starter spells the product out once;
 * the paid tiers just point at the tier below them, so the pricing page isn't
 * three copies of the same checklist.
 */
export type PlanFeatureBlock =
  | { kind: "list"; heading: string; items: ReadonlyArray<string> }
  | { kind: "inherits"; summary: string };

export interface PlanDescriptor {
  id: PlanId;
  /** What this plan stores as `users.plan` when active. `starter` is implicit (free_trial). */
  storedPlanValues: ReadonlyArray<string>;
  name: string;
  emoji: string;
  tagline: string;
  unfoldingsPerMonth: number;
  features: PlanFeatureBlock;
  pricing: PlanPricing | null;
}

/** The one place the monthly allowance is turned into prose. */
export function unfoldingsLabel(plan: PlanDescriptor): string {
  return `${plan.unfoldingsPerMonth} unfoldings / month`;
}

export const PLANS: ReadonlyArray<PlanDescriptor> = [
  {
    id: "starter",
    storedPlanValues: ["free_trial"],
    name: "Starter",
    emoji: "🌱",
    tagline: "For occasional reflection.",
    unfoldingsPerMonth: 5,
    features: {
      kind: "list",
      heading: "The full Unfold experience",
      items: [
        "Library, journal & calendar",
        "AI-guided reflection & insights",
        "Voice reflection",
        "Journal export",
      ],
    },
    pricing: null,
  },
  {
    id: "plus",
    storedPlanValues: ["plus"],
    name: "Plus",
    emoji: "✨",
    tagline: "For a regular reflection practice.",
    unfoldingsPerMonth: 50,
    features: {
      kind: "inherits",
      summary: "Everything in Starter, with more room to reflect.",
    },
    pricing: { monthly: 7, quarterlyTotal: 21, quarterlyMonthlyEquivalent: 7 },
  },
  {
    id: "premium",
    storedPlanValues: ["premium", "subscriber", "unlimited_trial"],
    name: "Premium",
    emoji: "🚀",
    tagline: "For frequent, deeper reflection.",
    unfoldingsPerMonth: 120,
    features: {
      kind: "inherits",
      summary: "Everything in Plus, with our highest reflection limit.",
    },
    pricing: { monthly: 17, quarterlyTotal: 51, quarterlyMonthlyEquivalent: 17 },
  },
];

/** Tier ordering for comparing plans. Higher rank = more premium. */
const PLAN_RANK: Record<PlanId, number> = { starter: 0, plus: 1, premium: 2 };

/** Numeric rank of a plan tier, for upgrade/downgrade comparisons. */
export function planRank(id: PlanId): number {
  return PLAN_RANK[id];
}

/** True when moving from `from` to `to` is an upgrade (a higher tier). */
export function isUpgrade(from: PlanId, to: PlanId): boolean {
  return planRank(to) > planRank(from);
}

/** Resolve the user's current plan card, defaulting to Starter when unset/free. */
export function getCurrentPlanId(user: AuthUser | null | undefined): PlanId {
  const stored = user?.plan;
  if (!stored) return "starter";
  for (const plan of PLANS) {
    if (plan.storedPlanValues.includes(stored)) return plan.id;
  }
  return "starter";
}
